import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { AddressService } from "../services/AddressService";
import ControlledField from "../../../shared/components/controlledField/ControlledField";

export default function AddressForm(props) {

  const { 
    onChange,
    control,
    setValue,
    getValues,
    defaultAddressValues,
    resident,
    disabled,
  } = props;

  const [streetOptions, setStreetOptions] = useState([
    {name: "", value: ""},
    {name: "SDF et vit à l'étranger", value: "SDF et vit à l'étranger"},
  ]);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [euCountryOptions, setEuCountryOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(defaultAddressValues?.street);
  
  const { watch } = useFormContext(); 

  const parseIfPossible = (value) => {
    if (typeof value !== 'string') return '';
    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
  }

  const fetchStreets = async (input) => {
    if (!input || input.length < 3) return;
    setLoading(true);
    try {
      const res = await AddressService.searchStreet(input, 20);
      setStreetOptions([
        {name: "", value: ""},
        {name: "SDF et vit à l'étranger", value: "SDF et vit à l'étranger"},
        ...res.data.sort((a, b) => a.name.localeCompare(b.name, 'fr') || a.locality.localeCompare(b.locality, 'fr'))
    ]);
    } catch (e) {
      console.error("Erreur chargement rues :", e);
    } finally {
      setLoading(false);
    }
  };

  const fetAllMunicipalities = async () => {
    try {
      const res = await AddressService.getMunicipalities();
      const sortedOptions = [
        ...(res.data || [])
          .sort((a, b) =>
            a.name.localeCompare(b.name, 'fr') ||
            a.locality.localeCompare(b.locality, 'fr')
          )
          .map(item => ({
            ...item,
            name: item.name,
            value: item.name
          }))
      ];
      setMunicipalityOptions(sortedOptions);
    } catch (e) {
      console.error("Erreur chargement des communes :", e);
    }
  };

  const initializeEuCountries = () => {
    const EU_COUNTRIES = [
      { name: "Allemagne", id: 19 },
      { name: "Autriche", id: 126 },
      { name: "Belgique", id: 133 },
      { name: "Bulgarie", id: 144 },
      { name: "Chypre", id: 166 },
      { name: "Croatie", id: 164 },
      { name: "Danemark", id: 167 },
      { name: "Espagne", id: 206 },
      { name: "Estonie", id: 8 },
      { name: "Finlande", id: 13 },
      { name: "France", id: 14 },
      { name: "Grèce", id: 23 },
      { name: "Hongrie", id: 37 },
      { name: "Irlande", id: 43 },
      { name: "Italie", id: 46 },
      { name: "Lettonie", id: 59 },
      { name: "Lituanie", id: 65 },
      { name: "Luxembourg", id: 66 },
      { name: "Malte", id: 74 },
      { name: "Pays-Bas", id: 93 },
      { name: "Pologne", id: 176 },
      { name: "Portugal", id: 177 },
      { name: "République tchèque", id: 171 },
      { name: "Roumanie", id: 181 },
      { name: "Slovaquie", id: 200 },
      { name: "Slovénie", id: 201 },
      { name: "Suède", id: 212 }
    ];
    
    const euOptions = EU_COUNTRIES
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      .map(country => ({
        name: country.name,
        value: country.id
      }));
    
    setEuCountryOptions(euOptions);
  };

  useEffect(() => {
    fetAllMunicipalities().then(() => {
      if (defaultAddressValues && setValue) {
        Object.entries(defaultAddressValues)?.forEach(([key, value]) => setValue(key, (value || '')));
      }
    });
    initializeEuCountries();
  }, []);

  useEffect(() => {
    if (!streetOptions.some(opt => opt.name === defaultAddressValues?.street)) {
      setStreetOptions(prev => [
          ...prev,
          { name: defaultAddressValues?.street, value: defaultAddressValues?.street }
      ]);
    }
    setInputValue(defaultAddressValues?.street);
  }, [defaultAddressValues?.street]);

  useEffect(() => {
    if (disabled) {
      setInputValue(defaultAddressValues?.street);
    }
  }, [disabled]);

  // Fonction helper pour déterminer le type de résident
  const getResidentType = () => {
    const residentValue = watch("resident") || resident;
    
    if (!residentValue) return '';
    
    // Si c'est déjà un objet
    if (typeof residentValue === "object" && residentValue.description) {
      return residentValue.description;
    }
    
    // Si c'est une chaîne JSON
    if (typeof residentValue === "string") {
      try {
        const parsed = JSON.parse(residentValue);
        return parsed?.description || '';
      } catch (e) {
        // Si ce n'est pas du JSON, retourner la valeur directement
        return residentValue;
      }
    }
    
    return '';
  };


  return (
    <>
        {/* Commune (pour les résidents du Luxembourg) */}
        {getResidentType() === "Luxembourg" && (
          <Box>
              <ControlledField
                  key="municipality"
                  name="municipality"
                  type="select"
                  label="Commune"
                  options={municipalityOptions}
                  control={control}
                  rules={{
                      required: (!watch("municipality") || (typeof watch("municipality") === 'string' && watch("municipality").trim() === "") || (typeof watch("municipality") !== 'string' && !watch("municipality"))) && !disabled
                          ? "Requis pour résidents luxembourgeois" 
                          : false
                  }}
                  onChange={(e) => { 
                      setValue("municipality", e.target.value, { shouldValidate: true });
                      props.onChange?.({ target: { name: "municipality", value: e.target.value } });
                  }}
                  disabled={disabled}
                  value={municipalityOptions.some(opt => opt.value === watch("municipality")) ? watch("municipality") : ""}
              />
          </Box>
        )}

        {/* Pays UE - affiché seulement pour les résidents UE */}
        {getResidentType() === "UE" && (
          <Box>
              <ControlledField
                  key="euCountry"
                  name="euCountry"
                  type="select"
                  label="Pays UE"
                  options={euCountryOptions}
                  control={control}
                  rules={{
                      required: (!watch("euCountry") || (typeof watch("euCountry") === 'string' && watch("euCountry").trim() === "") || (typeof watch("euCountry") !== 'string' && !watch("euCountry"))) && !disabled
                          ? "Requis pour résidents UE" 
                          : false
                  }}
                  onChange={(e) => { 
                      setValue("euCountry", e.target.value, { shouldValidate: true });
                      props.onChange?.({ target: { name: "euCountry", value: e.target.value } });
                  }}
                  disabled={disabled}
                  value={euCountryOptions.some(opt => opt.value === watch("euCountry")) ? watch("euCountry") : ""}
              />
          </Box>
        )}

        {/* Street number */}
        {/*<Box>
            <Controller
                name="streetNumber"
                control={control}
                render={({ field }) => (
                    <TextField 
                        {...field}
                        name="streetNumber"
                        label="Numéro de rue" 
                        fullWidth
                        value={field.value ?? ""}
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          props.onChange?.({ target: { name: "streetNumber", value: e.target.value } });
                        }}
                    />
                )}
            />
        </Box>*/}

        {/* Autocomplete rue */}
        {/*<Box>
            <Controller
                name="street"
                control={control}
                render={({ field, fieldState }) => (
                    <Autocomplete
                        {...field}
                        name="street"
                        options={streetOptions}
                        loading={loading}                       
                        value={streetOptions.find((opt) => opt.name === watch("street")) ?? { name: "", value: "" }}
                        inputValue={(typeof inputValue === 'number') ? inputValue?.toString() : inputValue}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === "input") {
                            const safeValue = typeof newInputValue === 'string' ? newInputValue : newInputValue?.toString() || '';
                            if ((safeValue?.length > 1) && (safeValue !== "SDF et vit à l'étranger")) {
                              fetchStreets(safeValue);
                            }
                            setInputValue(safeValue);
                            field.onChange({target: { name: "street", value: safeValue }});
                            if (props.onChange) {
                              props.onChange?.({target: {name: "street", value: safeValue}});
                            }
                          }
                        }}
                        onChange={(event, newValue) => {
                          field.onChange(newValue);
                          if (newValue?.name === "SDF et vit à l'étranger") {
                            setValue("streetNumber", "");
                          }
                          setValue("zipcode", newValue?.zipcode || "");
                          setValue("locality", newValue?.locality || "");
                          setValue("municipality", newValue?.municipality || "");
                          if (props?.onChange) {
                            if (typeof newValue === "string") {
                              props?.onChange?.({target: {name: "street", value: newValue || ''}});
                              setInputValue(newValue);
                            } else if (newValue && (typeof newValue === "object")) {
                              setInputValue(newValue?.name);
                              if (newValue?.name === "SDF et vit à l'étranger") {
                                props?.onChange?.({target: {name: "streetNumber", value: ""}});
                              }
                              props?.onChange?.({target: {name: "street", value: newValue?.name || ''}});
                              props?.onChange?.({target: {name: "zipcode", value: newValue?.zipcode || ''}});
                              props?.onChange?.({target: {name: "locality", value: newValue?.locality || ''}});
                              props?.onChange?.({target: {name: "municipality", value: newValue?.municipality || ''}});
                            } else if (!newValue) {
                              props?.onChange?.({target: {name: "street", value: ""}});
                              setInputValue("");
                            }
                          } else {
                            setInputValue(newValue?.name || "");
                          }
                        }}
                        getOptionLabel={(option) => {
                          if (!option || typeof option !== 'object') {
                            return typeof option === 'number' ? option?.toString() : '';
                          }
                          const name = option.name ?? '';
                          const zipcode = option.zipcode ? `${option.zipcode}` : '';
                          const locality = option.locality ? ` ${option.locality}` : '';
                          const municipality = option.municipality ? `${option.municipality}` : '';
                          return (zipcode) ? `${name} (${zipcode} ${locality}, ${municipality})` : `${name}`;
                        }}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value || typeof option !== 'object' || typeof value !== 'object') {
                            return false;
                          }
                          const optionName = typeof option.name === 'string' ? option.name : '';
                          const valueName = typeof value.name === 'string' ? value.name : '';
                          return optionName === valueName;
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Rue"
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                    <>
                                        {loading ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                    ),
                                }}
                            />
                        )}
                        disabled={disabled}
                    />
                )}
            />
        </Box>*/}

        {/* Code postal */}
        {/*<Box>
            <Controller
                name="zipcode"
                control={control}
                render={({ field }) => (
                    <TextField 
                        {...field}
                        name="zipcode"
                        label="Code Postal" 
                        fullWidth
                        value={field.value ?? ""}
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          props.onChange?.({ target: { name: "zipcode", value: e.target.value } });
                        }}
                    />
                )}
            />
        </Box>*/}

        {/* Ville */}
        {/*<Box>
            <Controller
                name="locality"
                control={control}
                render={({ field }) => (
                    <TextField 
                        {...field}
                        name="locality"
                        label="Ville" 
                        fullWidth
                        value={field.value ?? ""}
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          props.onChange?.({ target: { name: "locality", value: e.target.value } });
                        }}
                    />
                )}
            />
        </Box>*/}
    </>
  );
}
